import {ssrSafeWindow} from '@github-ui/ssr-utils'
/**
 * Reloads the current page with a reload parameter to avoid infinite loops
 */
export const reloadPage = () => {
  const reloadParameter = ['reload', '1'] as const
  const url = new URL(window.location.href, window.location.origin)

  /**
   * Return early when the parameter already exists to avoid an infinite loop
   */
  if (url.searchParams.has(...reloadParameter)) {
    return
  }

  url.searchParams.set(...reloadParameter)
  // Reload the page
  ssrSafeWindow?.location.assign(url)
}

type RepositoryResult<T> = {ok: true; value: T} | {ok: false; errors: unknown} | null | undefined
type OkayResult<T> = {ok: true; value: T}

/**
 * Handles repository data errors and returns the successful result or undefined.
 *
 * @param repositoryData The repository result from a GraphQL query with @catch(to: RESULT)
 * @param options.shouldReloadOnError When true (default), reloads the page for NOT_FOUND,
 *   FORBIDDEN, and AUTHENTICATION errors so the server can render a 404 or login redirect.
 *   Set to false for embedded contexts (e.g. side panels) where a reload would disrupt the
 *   parent page — errors return undefined instead, letting the caller render appropriate UI.
 * @returns The successful result, or undefined if there was an error
 */
export function handleRepositoryDataError<T>(
  repositoryData: RepositoryResult<T>,
  options?: {
    shouldReloadOnError?: boolean
    subIssueFromOptimisticUpdate?: boolean
  },
): OkayResult<T> | undefined {
  const {shouldReloadOnError = true} = options || {}

  if (
    !repositoryData ||
    (typeof repositoryData === 'object' && 'ok' in repositoryData && repositoryData.ok === false)
  ) {
    const errors = (repositoryData as {errors?: unknown[]})?.errors

    const hasTypedError = (type: string) =>
      errors?.some(e => e && typeof e === 'object' && 'type' in e && e.type === type)

    const hasSAMLError = hasTypedError('FORBIDDEN')
    const hasAuthenticationError = hasTypedError('AUTHENTICATION')
    const hasNotFoundError = hasTypedError('NOT_FOUND')

    // NOT_FOUND: reload when on a full page (server handles 404), otherwise return undefined
    // so the caller can show appropriate UI (e.g. side panels render "not found" inline)
    if (hasNotFoundError) {
      if (shouldReloadOnError) {
        reloadPage()
      }
      return undefined
    }

    if (shouldReloadOnError && (hasSAMLError || hasAuthenticationError)) {
      reloadPage()
      return undefined
    }

    // Errors without a recognized type (e.g. transient backend failures like {"path":["repository"]})
    // are treated as retriable — reload the page for an automatic retry. reloadPage() is loop-safe.
    const hasRecognizedType = errors?.some(e => e && typeof e === 'object' && 'type' in e)
    if (!hasRecognizedType) {
      if (!options?.subIssueFromOptimisticUpdate) {
        reloadPage()
      }
      return undefined
    }

    // We have data errors with a type we don't handle — throw to let the error boundary handle it
    throw new Error(`Repository data error: ${JSON.stringify(errors)}`)
  }

  return repositoryData
}
